import { useMemo, useCallback } from "react";
import { message } from "antd";
import { useGetCaptureQuery } from "../../actions/capture";
import { useGetDetectionQuery } from "../../actions/detection";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { CatalogItem } from "../../types";
import { downloadCatalogItems } from "../../hooks/useCatalogDownload";
import { isDetection } from "./isDetection";
import { generateDisplayId } from "../../utils/displayId";
import { useRouteType } from "./useRouteType";

export function useSpecimenData(id: string | undefined) {
  const { isMeshRoute } = useRouteType();

  const { data: detectionData, isLoading: isLoadingDetection } =
    useGetDetectionQuery({ id: id || "" }, { skip: isMeshRoute || !id });

  const captureId = isMeshRoute ? id || "" : detectionData?.capture_id || "";

  const { data: captureData, isLoading: isLoadingCapture } = useGetCaptureQuery(
    { id: captureId },
    { skip: !captureId },
  );

  const { data: devicesData } = useListDevicesQuery();
  const { data: trapSitesData } = useListTrapSitesQuery();

  const isLoading = isMeshRoute ? isLoadingCapture : isLoadingDetection;
  const item = isMeshRoute ? captureData : detectionData;

  const deviceName = useMemo(() => {
    if (!captureData?.device || !devicesData?.items) return "";
    const device = devicesData.items.find((d) => d.uuid === captureData.device);
    return device?.display_name || "";
  }, [captureData?.device, devicesData?.items]);

  const trapSite = useMemo(() => {
    if (!captureData?.trap_site_id || !trapSitesData?.items) return {};
    const trapSite = trapSitesData.items.find(
      (s) => s._id === captureData.trap_site_id,
    );
    return trapSite;
  }, [captureData?.trap_site_id, trapSitesData?.items]);

  const displayTitle = useMemo(() => {
    if (!item) return undefined;
    if (isMeshRoute) {
      return generateDisplayId({ captureUuid: item.uuid });
    }
    if (isDetection(item)) {
      return generateDisplayId({
        captureUuid: item.capture_uuid,
        specimenUuid: id,
      });
    }
    return undefined;
  }, [isMeshRoute, id, item]);

  const captureCatalogItem = useMemo(() => {
    if (!captureData) return undefined;
    return {
      ...captureData,
      source: "capture",
      imageType: "Mesh",
      img: captureData.img ?? "",
      device_name: deviceName,
      trap_site_name: trapSite?.display_name || "",
    } as CatalogItem;
  }, [captureData, deviceName, trapSite]);

  const catalogItem = useMemo<CatalogItem | undefined>(() => {
    if (isMeshRoute && captureCatalogItem) {
      return { ...captureCatalogItem, _id: id || "" };
    }
    if (!isMeshRoute && detectionData) {
      return {
        ...detectionData,
        _id: id || "",
        source: "detection",
        imageType: "Mosquito",
        img: detectionData.img ?? "",
        device: captureData?.device ?? "",
        trap_site_id: captureData?.trap_site_id ?? "",
        device_name: deviceName,
        trap_site_name: trapSite?.display_name || "",
      } as CatalogItem;
    }
    return undefined;
  }, [
    isMeshRoute,
    captureCatalogItem,
    detectionData,
    captureData,
    id,
    deviceName,
    trapSite,
  ]);

  const handleDownload = useCallback(async () => {
    if (!catalogItem) return;

    const items: CatalogItem[] = [catalogItem];

    if (!isMeshRoute && captureCatalogItem) {
      items.push(captureCatalogItem);
    }

    try {
      await downloadCatalogItems(items);
    } catch (error) {
      console.error("Download failed:", error);
      message.error("Download failed. Please try again.");
    }
  }, [catalogItem, captureCatalogItem, isMeshRoute]);

  return {
    isMeshRoute,
    isLoading,
    item,
    displayTitle,
    catalogItem,
    handleDownload,
    trapSite,
    deviceId: captureData?.device ?? "",
  };
}
